import { useCallback, useState } from "react";
import styled from "@emotion/styled";
import { throttle, debounce } from "lodash";
import {
  useRegisterTeamForTournamentMutation,
  useSearchGamersLazyQuery,
} from "generated/graphql";
import Modal from "components/Modal";
import NewSoloRegistration from "./NewSoloRegistration";
import NewTeamRegistration from "./NewTeamRegistration";

const ModalContentWrapper = styled.div`
  padding: 0.5rem;
`;

function RegistrationModal({
  setModalVisibility,
  membersInExistingTournamentTeams,
  teamId,
  tournamentId,
  tournamentData,
  tournamentDetailsMap,
  setIsJustRegistered,
}: any) {
  // Mutations
  const [findGamers, { loading: findingGamers, data: gamers }] =
    useSearchGamersLazyQuery();
  const [
    registerTeamForTournamentMutation,
    { loading: registering, data: registrationData },
  ] = useRegisterTeamForTournamentMutation();

  // State
  const [teamName, setTeamName] = useState();
  const [displayName, setDisplayName] = useState();
  const [avatar, setAvatar] = useState();
  const [teamMembers, setTeamMembers] = useState<any[]>([
    { label: "You", value: teamId, id: teamId, isFixed: true },
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttledSearch = useCallback(throttle(findGamers, 650), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(debounce(findGamers, 500), []);

  const closeModal = () => setModalVisibility(false);

  return (
    <Modal closeModal={() => setModalVisibility(false)}>
      <ModalContentWrapper>
        <h1 style={{ padding: 0, margin: "0 auto", textAlign: "center" }}>
          Tournament Registration
        </h1>
        {/* Register with an existing team */}
        {/* <ExistingTeamRegistration /> */}
        {/* I want to create a new team */}
        {
          // @ts-ignore
          tournamentDetailsMap["teamType"]! !== "TEAM_SOLO" && (
            <NewTeamRegistration
              name={teamName}
              displayName={displayName}
              avatar={avatar}
              teamMembers={teamMembers}
              teamType={tournamentDetailsMap["teamType"]}
              isLoading={findingGamers}
              searchGamers={async (searchValue: string) => {
                if (searchValue.length === 0) {
                  return [];
                }
                let search;
                if (searchValue.length < 4) {
                  search = throttledSearch;
                } else {
                  search = debouncedSearch;
                }

                const searchTerm = `%${searchValue}%`;
                search({ variables: { searchTerm } });

                if (findingGamers) {
                  return [];
                }
                return gamers?.parties
                  ?.map(({ id, name, nickname }) => ({
                    label: `${name} (${nickname})`,
                    value: id,
                  }))
                  .filter(
                    (gamer) => !membersInExistingTournamentTeams.includes(gamer)
                  );
              }}
              updateFunctions={{
                setTeamName,
                setDisplayName,
                setAvatar,
                setTeamMembers: (
                  member: {
                    label: string;
                    value: string;
                    id: string;
                    isFixed?: boolean;
                  }[],
                  actionMeta: any
                ) => {
                  switch (actionMeta.action) {
                    case "pop-value":
                    case "remove-value":
                      if (actionMeta.removedValue.isFixed) {
                        return;
                      }
                      setTeamMembers(
                        teamMembers.filter(
                          (mem: any) => mem === actionMeta.removedValue.value
                        )
                      );
                      return;
                    case "clear":
                      setTeamMembers(teamMembers.filter((m) => m.isFixed));
                      break;
                  }
                  setTeamMembers(
                    Array.from(new Set([...teamMembers, ...member]))
                  );
                },
              }}
              isSubmitting={registering}
              handleSubmit={async (regTeamId: string) => {
                const result = await registerTeamForTournamentMutation({
                  variables: {
                    input: {
                      teamId: regTeamId,
                      tournamentId,
                    },
                  },
                });
                if (result.data?.registerTeamForTournament) {
                  closeModal();
                  setIsJustRegistered(true);
                }
                if (result.errors) {
                  console.error(result.errors);
                }
              }}
            />
          )
        }
        {
          // @ts-ignore
          tournamentDetailsMap["teamType"]! === "TEAM_SOLO" && (
            <NewSoloRegistration
              tournamentName={tournamentData?.tournament?.name!}
              registering={registering}
              showThankYou={
                !!registrationData?.registerTeamForTournament?.message
              }
              closeModal={() => {
                setIsJustRegistered(true);
                closeModal();
              }}
              onClick={() => {
                try {
                  registerTeamForTournamentMutation({
                    variables: {
                      input: {
                        teamId,
                        tournamentId,
                      },
                    },
                    onCompleted: () => {
                      closeModal();
                      document.location.reload();
                    },
                    refetchQueries: ['GetTournament']
                  });
                  setIsJustRegistered(true);
                  if (!!registrationData?.registerTeamForTournament?.message) {
                    console.log("Close modal");
                    closeModal();
                  }
                } catch (err) {
                  console.error(err);
                }
              }}
            />
          )
        }
      </ModalContentWrapper>
    </Modal>
  );
}

export default RegistrationModal;
